import { registerBrick, BrickContext, CloudObject, QueryResult} from 'olympe';
import { getLogger, ReactBrick, useProperty, useMUITheme } from '@olympeio/core'
import React, {useEffect, useState} from 'react';
import { combineLatest } from 'rxjs';
import {
    DataGrid,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridFooter,
    GridFooterContainer,
    GridCheckIcon, GridCloseIcon,
    getGridStringOperators
} from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import { TextField } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { getIcon } from '@olympeio-extensions/commons';
import { format} from "date-fns";
import {firstValueFrom} from 'rxjs';
import {Contains, EndsWith, Equal, InArray, StartsWith} from "./serializerHelpers";


export default class SapDatagrid extends ReactBrick {

    static cachedMetadata = {};

    static async getCachedValue($, cacheDataFetcher, key, prop, value) {
        if (SapDatagrid.cachedMetadata[key] === undefined) {
            await SapDatagrid.getCollection($, cacheDataFetcher, key);
        }
        return SapDatagrid.cachedMetadata[key]?.find(item => item['Number'] === value)?.[prop];
    }
    static async getCollection($, cacheDataFetcher, key) {
        const value = '';
        switch (key) {
            case 'ItemsGroupCode':
                SapDatagrid.cachedMetadata[key] = await SapDatagrid.fetchCacheData($, cacheDataFetcher, 'ItemGroups');
                break;
            default: getLogger('SAP Datagrid').warn(`Unknown cached collection: ${key}`);
        }
        return value;
    }

    /**
     * @override
     */
    setupExecution($) {
        return combineLatest([
            $.observe('Hidden', false)
        ]);
    }

    /**
     * @override
     */
    static getReactComponent($) {

        const logger = getLogger('SAP Datagrid');

        return (props) => {
            const [hidden] = props.values;

            const [rows, setRows] = useState([]);
            const dataFetcher = useProperty($, 'Data Fetcher');
            const cacheDataFetcher = useProperty($, 'Cache Data Fetcher');
            const pageSize = useProperty($, 'page-size', true);
            const currentPage = useProperty($, 'current-page');
            const totalRows = useProperty($, 'row count');
            const whereClause = useProperty($, 'where-clause');
            const properties = useProperty($, 'Properties');
            const columnsWidth = useProperty($, 'Columns Width');
            const columnsHeaders = useProperty($, 'Headers');
            const orderBy = useProperty($, 'Order By');
            const orderByInternal = useProperty($, 'order-by');
            const [columns, setColumns] = useState([]);
            const [lastDisplayedPage, setLastDisplayedPage] = useState(0);
            const [refreshInternal, setRefreshInternal] = useState(0);

            const onRefresh = () => {
                setRows([]);
                $.trigger('Refresh');
            };

            useEffect(() => {
                    if (properties && columnsHeaders) {
                        setColumns(SapDatagrid.getColumnsFromProps($, properties?.split(';'), (columnsWidth ?? '1')?.split(';'), columnsHeaders?.split(';')));
                    }
                },
                [properties, columnsWidth, columnsHeaders]
            );

            // Call refresh for the initial fetch of data and every time "refresh" event is called.
            const refresh = useProperty($, 'Refresh');
            useEffect(() => {
                if (lastDisplayedPage === (currentPage ?? 0)) {
                    setRows([]);
                    $.set('current-page', 0);
                }
                setRefreshInternal(Date.now());
            }, [refresh]);


            useEffect(
                () => {
                    if(dataFetcher && pageSize && columns.length > 0) {
                        const page = currentPage ?? 0;
                        if(totalRows === 0 || (rows.length !== totalRows && pageSize * (page+1) > rows.length))  {

                            // Do not fetch too often on initialisation or when user type in search field
                            clearTimeout($.get('debounce-id'));
                            $.set('debounce-id', setTimeout(() => {
                                $.set('Is Loading', true);
                                SapDatagrid.fetchData($, dataFetcher).then(async (result) => {
                                    console.log(result);
                                    $.set('row count', result?.['totalSize'] ?? 0);
                                    const newRows = []
                                    for (const [index, entry] of result?.['value']?.entries() || []) {
                                        newRows.push(await SapDatagrid.processRow($, cacheDataFetcher, index + rows.length, entry, columns));
                                    }
                                    setRows(rows.concat(newRows));
                                    $.set('Is Loading', false);
                                    setLastDisplayedPage(page);
                                }).catch((e) => {
                                    $.set('Is Loading', false);
                                    logger.error(`Error occurred while fetching data: ${e}`);
                                });
                            }, 300));
                        }

                    }
                },
                [refreshInternal, dataFetcher, pageSize, currentPage, columns, orderBy, orderByInternal]
            );

            useEffect(
                () => {
                    setRows([]);
                    $.trigger('Refresh');
                },
                [whereClause, orderByInternal]
            );

            return !hidden && (
                <div style={{ height: '100%', width: '100%' }}>
                    <SapDatagrid.MuiDataGrid
                        $={$}
                        columns={columns}
                        rows={rows}
                        onRefresh={onRefresh}
                    />
                </div>
            );
        }
    }

    /**
     * @private
     * @param {!BrickContext} $
     * @param {Object} dataFetcher
     * @return {!Promise<QueryResult>}
     */
    static fetchData($, dataFetcher) {
        const [startInput, page, itemsPerPage, sort, filter] = dataFetcher.getInputs();
        const [endOutput, dataOutput, errorOutput] = dataFetcher.getOutputs();
        const runner$ = $.runner(dataFetcher);
        runner$.set(page, $.get('current-page'));
        runner$.set(itemsPerPage, $.get('page-size'));
        runner$.set(sort, $.get('order-by') ?? $.get('Order By'));

        if ($.get('Where')?.trim() && $.get('where-clause')?.trim()) {
            runner$.set(filter, $.get('Where')?.trim() + ' and (' + $.get('where-clause') + ')');
        } else if ($.get('Where')?.trim()) {
            runner$.set(filter, $.get('Where')?.trim());
        } else {
            runner$.set(filter, $.get('where-clause'));
        }
        runner$.trigger(startInput);

        // either the control flow or error flow is emitted in the lambda function
        const successPromise = firstValueFrom(runner$.observe(endOutput)).then(() => {
            return runner$.get(dataOutput);
        });
        const errorPromise = firstValueFrom(runner$.observe(errorOutput));
        return Promise.race([
            successPromise,
            errorPromise
        ]).then((res) => {
            runner$.destroy(); // stop function execution
            return res;
        });
    }

    /**
     * @private
     * @param {!BrickContext} $
     * @param {Object} dataFetcher
     * @param {string} resourceStr
     * @return {!Promise<QueryResult>}
     */
    static fetchCacheData($, dataFetcher, resourceStr) {
        const [startInput, resource] = dataFetcher.getInputs();
        const [endOutput, dataOutput, errorOutput] = dataFetcher.getOutputs();
        const runner$ = $.runner(dataFetcher);
        runner$.set(resource, resourceStr);
        runner$.trigger(startInput);

        // either the control flow or error flow is emitted in the lambda function
        const successPromise = firstValueFrom(runner$.observe(endOutput)).then(() => {
            return runner$.get(dataOutput);
        });
        const errorPromise = firstValueFrom(runner$.observe(errorOutput));
        return Promise.race([
            successPromise,
            errorPromise
        ]).then((res) => {
            runner$.destroy(); // stop function execution
            return res;
        });
    }

    /**
     * @private
     * @param {!BrickContext} $
     * @param {!Array} properties
     * @param {!Array} columnsWidth
     * @param {!Array} columnsHeader
     * @return {!Array<!Object>}
     */
    static getColumnsFromProps($, properties, columnsWidth, columnsHeader) {
        return properties.map((property, index) => {
            return {
                editable: false,
                field: property?.trim(),
                headerName: columnsHeader?.[index]?.trim() ?? property,
                flex: Number(columnsWidth?.[index] ?? 1),
            };
        });
    }

    /**
     * @private
     * @param {BrickContext} $
     * @param {object} cacheDataFetcher
     * @param {number} index
     * @param {CloudObject} entry
     * @param {!Array<!Object>} columns
     * @return {!Object}
     */
    static async processRow($, cacheDataFetcher, index, entry, columns) {
        const rowObject = {
            id: index,
            filterable: true,
            entry: entry
        };

        // Append properties values, in the order defined
        for(const col of columns || []) {
            const paths = col.field.split('.');
            let value = entry;
            for (const subPath of paths) {
                if (value) {
                    if (subPath.includes('[')) {
                        const metadataKey = subPath.substring(0, subPath.indexOf('['));
                        const subProp = subPath.substring(subPath.indexOf('[') + 1, subPath.length - 1);
                        value = await SapDatagrid.getCachedValue($, cacheDataFetcher, metadataKey, subProp, value[metadataKey]) ?? '';
                    } else {
                        value = value[subPath];
                        if (Array.isArray(value)) {
                            value = value[0];
                        }
                    }
                }
            }
            rowObject[col.field] = value;
        }

        return rowObject;
    }
}

/**
 * @param {!BrickContext} $
 * @param {!Array<!Object>} rows
 * @param {!Array<!Object>} columns
 * @param {function()} onRefresh
 */
SapDatagrid.MuiDataGrid = ({ $, rows, columns, onRefresh }) => {
    const rowHeight = 40;
    const [pageSize, setPageSize] = useState();
    const [sortModel, setSortModel] = useState();
    const [currentColumns, setCurrentColumns] = useState(columns);
    const [hiddenColumns, setHiddenColumns] = useState();
    const height = useProperty($, 'Height');
    const width = useProperty($, 'Width');
    const hiddenColumnsString = useProperty($, 'Hidden Columns');
    const dateFormat = useProperty($, 'Date Format');

    // Refresh column width and height on component height or width change
    React.useEffect(() => {
        const actualPageSize = getPageSize(height, rowHeight);
        $.set('page-size', actualPageSize);
        setPageSize(actualPageSize);

        const cols = columns.map(column => {
            return {
                ...column,
                renderCell: (params) => {

                    const dateRegex = /(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d\.\d+([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))|(\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d([+-][0-2]\d:[0-5]\d|Z))/
                    let found = false;
                    try {
                        found = params?.value?.match(dateRegex)?.length > 0;
                    } catch (e) {}
                    let newValue;
                    if(found) {
                        try {
                            newValue =  format(new Date(params.value), dateFormat);
                        } catch (e) {
                            newValue = '-bad date or date format-';
                            getLogger('Dataset Editor').error('Incorrect Date Format: ' + e.message);
                        }
                    } else if (params.value === 'tYES') {
                        newValue = (<GridCheckIcon />);
                    } else if (params.value === 'tNO') {
                        newValue = (<GridCloseIcon />);
                    } else {
                        newValue = params.value;
                    }
                    return newValue;
                },
                filterOperators: getGridStringOperators().filter(
                    (operator) => !operator.value.includes('Empty')
                ),
                sortable: !column.field?.includes('.'),
                filterable: !column.field?.includes('.'),
            };
        });
        setCurrentColumns(cols);
    }, [height, width, columns]);


    React.useEffect(() => {
        if(hiddenColumnsString && currentColumns.length > 0) {
            setHiddenColumns(hiddenColumnsString.split(';').reduce((acc, cur) => {
                acc[cur] = false;
                return acc;
            }, {}));
        }
    }, [hiddenColumnsString, currentColumns]);

    /**
     * @param {number} height
     * @param {number} rowHeight
     * @return {number}
     */
    const getPageSize = (height, rowHeight) => {
        let margin = 144; // 144 = header + footer + toolbar
        if($.get('Hide Toolbar')) {
            margin -= 36;
        }
        const size = Math.floor((height - margin) / rowHeight);
        return size <= 0 ? 1 : size; // minimum 1 row per page
    };

    /**
     * @param {string} value
     */
    const search = (value) => {
        let where = '';
        if(value !== '') {
            where = columns
                .filter(col => !col.field?.includes('.'))
                .map(col => {
                    let field = col.field;
                    if (col.field.includes('[')) {
                        // If we're in the cached collection (the key contains a value between square brackets),
                        // Looking in the cached collection & sending the possible keys to server
                        field = col.field.substring(0, col.field.indexOf('['));
                        const possibleValues = SapDatagrid.cachedMetadata[field].filter(f => {
                            const cacheKey = col.field.substring(col.field.indexOf('[')+1, col.field.indexOf(']'));
                            return f[cacheKey].toLowerCase().includes(value.toLowerCase());
                        });
                        return new InArray(field, possibleValues.map(f => f['Number'])).toString();
                    } else {
                        // Normal case, just sending a contains
                        return new Contains(field, value).toString();
                    }
                })
                .filter(col => col !== '')
                .join(' or ');
        }
        $.set('where-clause', where);
    };


    /**
     * Selection: conditionally fire event On Selected
     * @param {!Object|Object[]} newSelection
     */
    const onSelectionModelChange = (newSelection) => {
        const objects = newSelection.map(id => rows[id]?.entry);

        if ($.get('Multiple Selection')) {
            if ((newSelection || []).length > 0) {
                $.set('Selected Value(s)', objects);
                $.trigger('On Selected');
            } else {
                $.set('Selected Value(s)', objects);
            }
        } else {
            if (objects[0] !== undefined) {
                $.set('Selected Value(s)', objects[0]);
                $.trigger('On Selected');
            } else {
                $.set('Selected Value(s)', null);
            }
        }
    };

    const onPaginationModelChange = (newPage) => {
        $.set('current-page', newPage);
    };

    const onFilterChange = (newFilter) => {
        let field = newFilter.items[0].columnField;
        const operator = newFilter.items[0].operatorValue;
        const value = newFilter.items[0].value;


        if (field.includes('[')) {
            // If we're in the cached collection (the key contains a value between square brackets),
            // Looking in the cached collection & sending the possible keys to server
            const key = field.substring(0, field.indexOf('['));
            const possibleValues = SapDatagrid.cachedMetadata[key].filter(f => {
                const cacheKey = field.substring(field.indexOf('[')+1, field.indexOf(']'));

                switch (operator) {
                    case 'contains': return f[cacheKey]?.toLowerCase().includes(value?.toLowerCase());
                    case 'equals': return f[cacheKey]?.toLowerCase() === value?.toLowerCase();
                    case 'startsWith': return f[cacheKey]?.toLowerCase().startsWith(value?.toLowerCase());
                    case 'endsWith': return f[cacheKey]?.toLowerCase().endsWith(value?.toLowerCase());
                    case 'isAnyOf': return value?.includes(f[cacheKey]);
                }
            });
            $.set('where-clause', new InArray(key, possibleValues.map(f => f['Number'])).toString());
        } else {
            // Normal case, just sending the value to server
            switch (operator) {
                case 'contains': $.set('where-clause', new Contains(field, value).toString()); break;
                case 'equals': $.set('where-clause', new Equal(field, value).toString()); break;
                case 'startsWith': $.set('where-clause', new StartsWith(field, value).toString()); break;
                case 'endsWith': $.set('where-clause', new EndsWith(field, value).toString()); break;
                case 'isAnyOf': $.set('where-clause', new InArray(field, value).toString()); break;
            }
        }
    };
    const onSortChange = (newSort) => {

        if(newSort[0]) {
            const { field, sort } = newSort[0];
            if (field.includes('[')) {
                $.set('order-by', field.substring(0, field.indexOf('[')) + ' ' + sort);
            } else {
                $.set('order-by', field + ' ' + sort);
            }
        } else {
            $.set('order-by', null);
        }
        setSortModel(newSort);
    };

    const CustomToolbar = () => {
        return (
            <GridToolbarContainer>
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
            </GridToolbarContainer>
        );
    };

    const CustomFooter = () => {
        return (
            <GridFooterContainer>
                <IconButton
                    aria-label="refresh"
                    variant="contained"
                    color="primary"
                    size="small"
                    style={{ paddingLeft: 10 }}
                    onClick={onRefresh}
                >{getIcon('refresh')}</IconButton>
                <GridFooter />
            </GridFooterContainer>
        );
    };

    const hideToolbar = useProperty($,'Hide Toolbar')
    const myComponents = {Footer: CustomFooter}
    if(!hideToolbar) {
        myComponents.Toolbar = CustomToolbar;
    }

    const theme = useMUITheme($);
    return (
        <ThemeProvider theme={theme}>
            <div style={{position: 'absolute', top: '0px', bottom: '0px', width: '100%'}}>
                <DataGrid
                    rows={rows}
                    columns={currentColumns}
                    rowHeight={rowHeight}
                    pageSize={pageSize}
                    components={myComponents}
                    columnVisibilityModel={hiddenColumns}
                    onColumnVisibilityModelChange={setHiddenColumns}
                    checkboxSelection={useProperty($, 'Multiple Selection')}
                    onSelectionModelChange={onSelectionModelChange}
                    rowsPerPageOptions={[useProperty($, 'page-size')]}
                    loading={useProperty($, 'Is Loading')}
                    page={useProperty($, 'current-page')}
                    rowCount={useProperty($, 'row count')}
                    onPageChange={onPaginationModelChange}
                    onFilterModelChange={onFilterChange}
                    sortModel={sortModel}
                    onSortModelChange={onSortChange}
                />
            </div>
            { !useProperty($, 'Hide Toolbar') && <SapDatagrid.Search search={search} valueOf={''} />}
        </ThemeProvider>
    );
};

/**
 * @param {boolean} isShown
 * @param {function(string)} search
 */
SapDatagrid.Search = ({ search }) => {
    return (
        <TextField
            id="outlined-basic"
            variant="standard"
            placeholder="search..."
            size="small"
            style={{
                height: '30px',
                position: 'absolute', right: '5px', top: '5px',
                display: 'block',
            }}
            onChange={(event) => search(event.target.value)}
        />
    );
};

registerBrick('018eb1a85c96733589b0', SapDatagrid);